import React, { useState } from 'react';
import LayoutDashboard from 'app/private/layout';
import { Field, InfoItem, PaperStyled, Title } from './index.styles';
import { Grid } from '@material-ui/core';
import Steps from '../../../../components/steps';
import PersonalInfoSkeleton from './components/personal-info/skeleton';
import Skeleton from '@material-ui/lab/Skeleton';
import IdDetailsSkeleton from './components/id-details/skeleton';
import _ from 'lodash';
import DocumentComponent from './components/documents/document-component';
import { EDocumentTypes } from 'types/entities/documents';
import IdDetails from './components/id-details';
import PersonalInfo from './components/personal-info';
import Ktp from './components/ktp';
import CftPepAndApprovalComponents from './components/approval';
import { users } from '../../../../types';
import useUsersDetail from '../users/hooks/use-users-detail';
import { useRouter } from '../../../../hooks/use-router';
import useUsersEkycDetailGet from '../users/hooks/use-user-ekyc-get-new';
import {
    EEkycStatuesesApi,
    IEKYCDetails,
    IEKYCUserFormat,
    IGetEKYCDetailsResponse,
    IPEPCFTCheck,
} from '../../../../types/entities/users';
import { placeholderImage } from './util/placeholder-image';
import timeFormat from 'utils/time-format';
import useCurrentSessionData from '../../../../hooks/use-current-session-data';

const STEPLIST = ['Personal Info', 'KTP', 'PEP & CFT'];

const EkycIndividual = () => {
    const [page, setPage] = React.useState(0);
    const [skipped, setSkipped] = React.useState(new Set<number>());
    const { location, history } = useRouter();
    const pathName = history.location.pathname;
    const userId = pathName.split('/')[2];
    const { permissions } = useCurrentSessionData();

    const { ekycUserFormat: passedUserFormatData, ekycData: passedEkycData } =
        location.state as {
            ekycUserFormat: IEKYCUserFormat;
            ekycData: IGetEKYCDetailsResponse;
        };
    const [ekycUserFormat, setEkycUserFormat] =
        useState<IEKYCUserFormat>(passedUserFormatData);
    const [ekycData, setEkycData] =
        useState<IGetEKYCDetailsResponse>(passedEkycData);
    let ekycDetails = ekycData?.ekycDetails as IEKYCDetails;
    if (ekycDetails) {
        // use idAddress if residentialAddress is not available to handle old data
        ekycDetails = {
            ...(ekycDetails as IEKYCDetails),
            residentialAddress:
                (ekycDetails as IEKYCDetails).residentialAddress ||
                (ekycDetails as IEKYCDetails).idAddress,
        };
    }
    const [updatedData, setUpdatedData] = useState<IEKYCDetails>(ekycDetails);

    const [userData, setUserData] = useState<users.TUsersDetail>();

    const { isLoading, isError } = useUsersDetail(userId, {
        onSuccess: (data) => {
            setUserData(data as users.TUsersDetail);
        },
    });

    const { refetch } = useUsersEkycDetailGet(userId, {
        onSuccess: (data) => {
            const parsedData = data as IGetEKYCDetailsResponse;
            setEkycData(parsedData);
            const ekycData = {
                id: parsedData.id,
                ekycDetails: parsedData.ekycDetails,
                ekycDocuments: [
                    {
                        type: EDocumentTypes.KTP,
                        imageData: {
                            content: parsedData.ktp,
                            name: 'ktpphoto.jpg',
                            type: 'image/jpeg',
                        },
                    },
                    {
                        type: EDocumentTypes.Selfie,
                        imageData: {
                            content: parsedData.selfie,
                            name: 'selfie.jpg',
                            type: 'image/jpeg',
                        },
                    },
                ],
                validations: parsedData.validations,
            } as IEKYCUserFormat;
            setEkycUserFormat(ekycData);
        },
    });

    const isStepSkipped = (step: number) => {
        return skipped.has(step);
    };

    const setNextPage = () => {
        let newSkipped = skipped;
        if (isStepSkipped(page)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(page);
        }

        setPage((prevPage) => prevPage + 1);
        setSkipped(newSkipped);
    };

    const setPrevPage = () => {
        setPage((prevPage) => prevPage - 1);
    };

    const handleStep = (step: number) => {
        setPage(step);
    };

    const handleSkip = () => {
        setPage((prevPage) => prevPage + 1);
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(page);
            return newSkipped;
        });
    };

    const isKycProcessing =
        ekycData.status === EEkycStatuesesApi.EKYC_STATUS_PROCESSING;
    // Allow edit if status is processing and user has edit permission (userEKYCEdit)
    const disableEditAndProcessing =
        !isKycProcessing || !permissions?.userEKYCEdit;

    const isKycNotVerified =
        ekycData.status === EEkycStatuesesApi.EKYC_STATUS_UNVERIFIED;

    const renderPage = () => {
        switch (page) {
            case 0:
                return (
                    <PersonalInfo
                        setNextPage={setNextPage}
                        handleSkip={handleSkip}
                        data={ekycDetails}
                        userData={userData}
                        updatedData={updatedData}
                        setUpdatedData={setUpdatedData}
                        disableEdit={disableEditAndProcessing}
                    />
                );
            case 1:
                return (
                    <Ktp
                        setNextPage={setNextPage}
                        handleSkip={handleSkip}
                        setPrevPage={setPrevPage}
                        data={ekycUserFormat}
                        refetch={refetch}
                        kycId={ekycData?.id}
                        userId={userId}
                    />
                );
            case 2:
                return (
                    <CftPepAndApprovalComponents
                        setPrevPage={setPrevPage}
                        data={
                            ekycUserFormat.validations
                                ?.pepCftValidation as IPEPCFTCheck
                        }
                        updatedData={updatedData}
                        refetch={refetch}
                        userId={userId}
                        disableProcessing={disableEditAndProcessing}
                    />
                );
            default:
                return;
        }
    };

    const skeletonKtp = {
        type: EDocumentTypes.KTP,
        imageData: {
            content: placeholderImage,
            name: 'KTP.jpg',
            type: 'image/jpeg',
        },
    };
    const currentKtp =
        ekycUserFormat?.ekycDocuments
            .filter((doc) => doc.type === EDocumentTypes.KTP)
            .find((item: any) => item.imageData) || skeletonKtp;

    return (
        <LayoutDashboard>
            <Title>Individual EKYC</Title>
            <Grid container spacing={2}>
                <Grid item xs={1}>
                    <Steps
                        statuses={['PENDING', 'PENDING', 'PENDING']}
                        activeStep={page}
                        isSkipped={() => false}
                        steps={STEPLIST}
                        handleStep={handleStep}
                    />
                </Grid>

                {isLoading ? (
                    <Grid container item xs={11} spacing={2}>
                        <Grid item xs={8}>
                            <PersonalInfoSkeleton />
                        </Grid>
                        <Grid item xs={4}>
                            <PaperStyled>
                                <div>
                                    {_.times(6, (index) => (
                                        <Field key={index}>
                                            <span className="title">
                                                <Skeleton width={170} />
                                            </span>
                                            <span className="value">
                                                <Skeleton />
                                            </span>
                                        </Field>
                                    ))}
                                </div>
                            </PaperStyled>
                            <PaperStyled>
                                <Skeleton
                                    variant="rect"
                                    width={320}
                                    height={200}
                                />
                                <IdDetailsSkeleton />
                            </PaperStyled>
                        </Grid>
                    </Grid>
                ) : isError ? (
                    <p>{'error'}</p>
                ) : (
                    <Grid item container xs={11} spacing={2}>
                        <Grid item xs={8}>
                            {renderPage()}
                        </Grid>
                        <Grid item xs={4}>
                            <PaperStyled>
                                <InfoItem>
                                    <span className="title">
                                        {'KYC Submission ID'}:
                                    </span>
                                    <span className="value">
                                        {isKycNotVerified ? '-' : ekycData?.id}
                                    </span>
                                </InfoItem>
                                <InfoItem>
                                    <span className="title">{'Created'}:</span>
                                    <span className="value">
                                        {isKycNotVerified
                                            ? '-'
                                            : timeFormat(
                                                  ekycData?.submittedAt as string,
                                                  false,
                                              )}
                                    </span>
                                </InfoItem>
                                <InfoItem>
                                    <span className="title">{'Updated'}:</span>
                                    <span className="value">
                                        {isKycNotVerified
                                            ? '-'
                                            : timeFormat(
                                                  ekycData?.submissionUpdatedAt as string,
                                                  false,
                                              )}
                                    </span>
                                </InfoItem>
                            </PaperStyled>
                            <PaperStyled>
                                <h5>{'KTP'}</h5>
                                <DocumentComponent
                                    kycData={currentKtp}
                                    title={'Submitted KTP'}
                                />
                                <IdDetails
                                    ekycDetails={
                                        ekycData.ekycDetails as IEKYCDetails
                                    }
                                    userData={userData}
                                />
                            </PaperStyled>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </LayoutDashboard>
    );
};

export default EkycIndividual;
